/**
 * The inline grammar, on its own with no filesystem behind it.
 *
 * Split out of `guide-content.ts` because that module reads content files and therefore
 * imports `node:fs` — and `parseInline` is needed by `components/inline-text.tsx`, which
 * a client component renders. Importing the parser dragged `node:fs` into the client
 * bundle and the build failed outright: "the chunking context does not support external
 * modules (request: node:fs)". The grammar has no business knowing about the disk, so it
 * moved rather than the import being worked around.
 *
 * `guide-content.ts` re-exports everything here, so existing call sites are unchanged.
 */

/*
 * The inline grammar, as one alternation:
 *   **bold**  *italic*  `code`  ^{sup}  [label](/href)
 *
 * Each alternative requires a non-empty body and a closing marker, so an unclosed
 * `**`, an empty `[]()` and a bare `*` all fail to match and survive as literal text.
 * A body may not contain its own marker, which is what makes runs nesting-free without
 * a stack: `**[a](/b)**` is bold text that happens to read like a link, not a link
 * inside bold.
 *
 * Superscript is braced (`^{1}`) rather than bare (`^1`) because footnote markers live
 * in prose that already contains carets and exponents — "10^6", "x^2". A bare marker
 * would silently eat the character after any of them; a braced one cannot collide with
 * anything the guides already say, and it is the only run whose delimiters are
 * asymmetric, so it can sit next to a digit without ambiguity.
 */
const INLINE_RUN =
  /\\([\\*`^_[\]])|\*\*((?:[^*\\]|\\.)+)\*\*|\*((?:[^*\\]|\\.)+)\*|`((?:[^`\\]|\\.)+)`|\^\{((?:[^{}\\]|\\.)+)\}|_\{((?:[^{}\\]|\\.)+)\}|\[((?:[^\]\\]|\\.)+)\]\(([^\s)]+)\)/g;

/** The characters a backslash can escape — every one that can start or end a run. */
const ESCAPABLE = /[\\*`^_[\]]/g;
const ESCAPED = /\\([\\*`^_[\]])/g;

/** Resolve the escapes inside a run body. */
function unescapeInline(text: string): string {
  return text.replace(ESCAPED, "$1");
}

/** One run of inline text. Runs never nest — see `parseInline`. */
export type GuideInline =
  | { type: "text"; text: string }
  | { type: "bold"; text: string }
  | { type: "italic"; text: string }
  | { type: "code"; text: string }
  | { type: "sup"; text: string }
  | { type: "sub"; text: string }
  | { type: "link"; text: string; href: string };

/**
 * A list item: a line, or a line with a list under it.
 *
 * Nesting is what a real outline looks like — "Organise by theme" with the four themes
 * under it — and flattening one loses the relationship, not just the indentation. The
 * plain-string form stays legal because it is what almost every item is.
 */
export function escapeInline(text: string): string {
  return text.replace(ESCAPABLE, "\\$&");
}

function runFromMatch(match: RegExpExecArray): GuideInline {
  const [, escaped, bold, italic, code, sup, sub, label, href] = match;
  if (escaped !== undefined) {
    return { type: "text", text: escaped };
  }
  if (bold !== undefined) {
    return { type: "bold", text: unescapeInline(bold) };
  }
  if (italic !== undefined) {
    return { type: "italic", text: unescapeInline(italic) };
  }
  if (code !== undefined) {
    return { type: "code", text: unescapeInline(code) };
  }
  if (sup !== undefined) {
    return { type: "sup", text: unescapeInline(sup) };
  }
  if (sub !== undefined) {
    return { type: "sub", text: unescapeInline(sub) };
  }
  return { type: "link", text: unescapeInline(label), href };
}

/** An inline run back as source. `parseInline` of the result reproduces the run. */
export function inlineRunToSource(run: GuideInline): string {
  const text = escapeInline(run.text);
  if (run.type === "bold") {
    return `**${text}**`;
  }
  if (run.type === "italic") {
    return `*${text}*`;
  }
  if (run.type === "code") {
    return `\`${text}\``;
  }
  if (run.type === "sup") {
    return `^{${text}}`;
  }
  if (run.type === "sub") {
    return `_{${text}}`;
  }
  if (run.type === "link") {
    return `[${text}](${run.href})`;
  }
  return text;
}

/** Append a run, merging it into the previous one when both are plain text. */
function pushRun(runs: GuideInline[], run: GuideInline): void {
  const last = runs.at(-1);
  if (run.type === "text" && last?.type === "text") {
    last.text += run.text;
    return;
  }
  runs.push(run);
}

/**
 * Split a string into inline runs.
 *
 * Total: anything the grammar does not recognise comes back as `text`, so malformed
 * markup renders as the characters the author typed rather than disappearing.
 * Adjacent text is merged, so an escape never splits a sentence into two runs.
 */
export function parseInline(source: string): GuideInline[] {
  const runs: GuideInline[] = [];
  let cursor = 0;
  INLINE_RUN.lastIndex = 0;
  let match = INLINE_RUN.exec(source);
  while (match !== null) {
    if (match.index > cursor) {
      pushRun(runs, { type: "text", text: source.slice(cursor, match.index) });
    }
    pushRun(runs, runFromMatch(match));
    cursor = match.index + match[0].length;
    match = INLINE_RUN.exec(source);
  }
  if (cursor < source.length) {
    pushRun(runs, { type: "text", text: source.slice(cursor) });
  }
  return runs;
}

/** The visible text of a string of inline markup, with the markers removed. */
export function inlineToPlainText(source: string): string {
  return parseInline(source)
    .map((run) => run.text)
    .join("");
}
